export interface CaseStudyStat {
  value: string;
  label: string;
}

export interface CaseStudy {
  slug: string;
  industry: string;
  label: string;
  title: string;
  titleAccent: string;
  subtitle: string;
  summary: string;
  challenge: string[];
  solution: string[];
  impact: string[];
  techStack: string[];
  stats: CaseStudyStat[];
  locationSlugs?: string[];
  serviceSlugs?: string[];
}

export const caseStudies: CaseStudy[] = [
  {
    slug: "law-firm-crm",
    industry: "LEGAL",
    label: "CASE STUDY · LEGAL",
    title: "Law firm operations",
    titleAccent: "restructured.",
    subtitle:
      "A CRM built for a 40-advocate practice to replace fragmented intake, scattered follow-up, and slow matter visibility.",
    summary:
      "The firm needed a workflow-native system, not a generic CRM. We redesigned intake, follow-up, case assignment, and internal visibility around how the team already worked.",
    challenge: [
      "New enquiries were being tracked across spreadsheets, WhatsApp, and individual inboxes.",
      "Senior advocates had limited visibility into intake velocity and follow-up quality.",
      "Admin effort was being spent on repetitive coordination instead of client service.",
    ],
    solution: [
      "Built a custom CRM with lead capture, matter intake, task routing, and role-based dashboards.",
      "Added workflow states for consultation, onboarding, document collection, and ongoing case progress.",
      "Centralised communication history so the team could see what happened before every follow-up.",
    ],
    impact: [
      "Client intake time dropped substantially because the first-call process became structured.",
      "Matter visibility improved across partners, associates, and support staff.",
      "The firm moved from reactive admin work to a predictable intake pipeline.",
    ],
    techStack: ["React", "TypeScript", "Node.js", "PostgreSQL", "Role-Based Access Control"],
    stats: [
      { value: "60%", label: "Faster Client Intake" },
      { value: "40", label: "Advocates Supported" },
      { value: "1", label: "Unified Matter Pipeline" },
    ],
    locationSlugs: ["mumbai", "london", "new-york"],
    serviceSlugs: ["crm-solutions", "enterprise-portals"],
  },
  {
    slug: "real-estate-ai",
    industry: "REAL ESTATE",
    label: "CASE STUDY · REAL ESTATE",
    title: "Lead qualification",
    titleAccent: "automated.",
    subtitle:
      "An AI-led follow-up system for a property broker handling multi-channel leads and inconsistent response times.",
    summary:
      "The broker was spending heavily on lead generation but losing momentum before site visits were booked. We designed a qualification workflow that reacted in minutes, not hours.",
    challenge: [
      "New leads from ads and listing platforms were not being contacted consistently.",
      "Agents were spending too much time qualifying low-intent prospects manually.",
      "The team lacked a reliable process for moving from enquiry to site visit.",
    ],
    solution: [
      "Connected inbound lead sources into a single routing pipeline.",
      "Introduced AI-driven first-response and qualification prompts to sort hot, warm, and cold leads.",
      "Triggered agent assignment and scheduling flows once intent thresholds were met.",
    ],
    impact: [
      "More qualified prospects reached human agents while interest was still high.",
      "The sales team spent more time on site visits and less time on repetitive triage.",
      "Follow-up consistency improved across all active campaigns.",
    ],
    techStack: ["React", "Automation Workflows", "CRM Sync", "WhatsApp Messaging", "Lead Scoring"],
    stats: [
      { value: "3×", label: "More Site Visits Booked" },
      { value: "60s", label: "First Response Window" },
      { value: "1", label: "Unified Lead Queue" },
    ],
    locationSlugs: ["dubai", "mumbai", "miami"],
    serviceSlugs: ["ai-chatbots", "digital-marketing-seo"],
  },
  {
    slug: "ai-call-centre",
    industry: "AI AUTOMATION",
    label: "CASE STUDY · AI AUTOMATION",
    title: "Voice follow-up",
    titleAccent: "at scale.",
    subtitle:
      "An AI call centre workflow for insurance follow-up that replaced repetitive human outreach with consistent, high-volume automation.",
    summary:
      "The client needed repeatable outbound follow-up without staffing a large agent floor. We built an AI voice workflow that handled qualification, reminders, and record updates around the clock.",
    challenge: [
      "Manual calling capacity was limited and inconsistent across the month.",
      "Lead and policy follow-up depended too heavily on staff availability.",
      "Call summaries and updates were not landing reliably in the operating system.",
    ],
    solution: [
      "Implemented AI voice agents for outbound follow-up and qualification flows.",
      "Connected transcripts and summaries back into downstream records for operational visibility.",
      "Designed escalation paths so calls could hand off cleanly when human intervention was needed.",
    ],
    impact: [
      "The client gained predictable outreach volume without adding headcount.",
      "Operational teams could review outcomes and handoffs in one place.",
      "The outreach system remained active beyond standard business hours.",
    ],
    techStack: ["Twilio", "LLM Orchestration", "Speech-to-Text", "Automation Workflows", "CRM Updates"],
    stats: [
      { value: "5,000", label: "Calls Per Month" },
      { value: "24/7", label: "Availability" },
      { value: "0", label: "Human Agents Required For Baseline Flow" },
    ],
    locationSlugs: ["dubai", "abu-dhabi", "houston"],
    serviceSlugs: ["ai-chatbots", "natural-language-processing"],
  },
  {
    slug: "ecommerce",
    industry: "E-COMMERCE",
    label: "CASE STUDY · E-COMMERCE",
    title: "Average order value",
    titleAccent: "lifted.",
    subtitle:
      "A Shopify-based D2C experience with AI-informed recommendations and stronger merchandising logic.",
    summary:
      "The brand wanted a storefront that looked premium and sold more intelligently. We focused on conversion architecture, merchandising, and better recommendation placement.",
    challenge: [
      "The storefront experience was visually dated and under-communicated product value.",
      "Cross-sell and upsell behavior was inconsistent across collection and product pages.",
      "The team needed a cleaner foundation for marketing and campaign traffic.",
    ],
    solution: [
      "Redesigned the storefront for clarity, speed, and stronger decision support.",
      "Introduced recommendation modules aligned to cart and product context.",
      "Improved content hierarchy, conversion cues, and purchase flow friction points.",
    ],
    impact: [
      "Product discovery improved across high-intent pages.",
      "Order value increased through better recommendation placement and bundling logic.",
      "The team got a stronger merchandising base for future campaign growth.",
    ],
    techStack: ["Shopify", "Theme Customisation", "Recommendation Logic", "Analytics", "Conversion UX"],
    stats: [
      { value: "40%", label: "Increase In Order Value" },
      { value: "1", label: "Optimised Storefront System" },
      { value: "24/7", label: "Always-On Conversion Layer" },
    ],
    locationSlugs: ["amsterdam", "barcelona", "new-york"],
    serviceSlugs: ["shopify-ecommerce", "digital-marketing-seo"],
  },
  {
    slug: "healthcare-patient-management",
    industry: "HEALTHCARE",
    label: "CASE STUDY · HEALTHCARE",
    title: "Patient operations",
    titleAccent: "stabilised.",
    subtitle:
      "A patient management workflow for a multi-specialty clinic focused on appointment discipline, follow-up visibility, and operational reliability.",
    summary:
      "The clinic needed a steadier patient experience across front-desk intake, reminders, and visit coordination. We organised the workflow into a cleaner operational system.",
    challenge: [
      "Appointments and follow-ups were being coordinated across disconnected tools and manual handoffs.",
      "Missed appointments created avoidable downtime and weak capacity planning.",
      "The team had limited visibility into patient communication and scheduling history.",
    ],
    solution: [
      "Designed a structured patient management flow for registration, appointments, reminders, and internal updates.",
      "Improved operational visibility around visit status and communication history.",
      "Created a more reliable day-to-day scheduling process for the clinic team.",
    ],
    impact: [
      "The clinic reduced missed appointments through better reminder and coordination discipline.",
      "Staff had a clearer operational view of patient flow and upcoming commitments.",
      "The patient experience became more consistent across booking and follow-up touchpoints.",
    ],
    techStack: ["Scheduling Workflows", "Patient Records", "Reminder Automation", "Operational Dashboard"],
    stats: [
      { value: "0", label: "Missed Appointments In Pilot Window" },
      { value: "1", label: "Unified Patient Workflow" },
      { value: "24/7", label: "Reminder Coverage" },
    ],
    locationSlugs: ["boston", "london", "delhi"],
    serviceSlugs: ["healthcare-tech", "business-process-automation"],
  },
  {
    slug: "manufacturing-erp",
    industry: "MANUFACTURING",
    label: "CASE STUDY · MANUFACTURING",
    title: "Factory operations",
    titleAccent: "connected.",
    subtitle:
      "A custom ERP for an auto-parts MSME to bring inventory, purchasing, and production visibility into one system.",
    summary:
      "The manufacturer was losing money through scattered stock control and inconsistent planning. We built a tighter ERP layer for day-to-day operational clarity.",
    challenge: [
      "Inventory records were fragmented across teams and spreadsheets.",
      "Purchasing and production planning lacked a dependable shared source of truth.",
      "Operational waste was being discovered too late to prevent it.",
    ],
    solution: [
      "Implemented a central ERP flow for inventory, procurement, and production tracking.",
      "Improved visibility around stock movement, material needs, and operational status.",
      "Created a reporting layer that made waste and bottlenecks easier to spot early.",
    ],
    impact: [
      "Inventory waste reduced because stock and planning data became more trustworthy.",
      "Decision-making improved across procurement and production teams.",
      "Leadership gained a clearer picture of operational performance in one place.",
    ],
    techStack: ["ERP Workflows", "Inventory Control", "Procurement Tracking", "Operational Reporting"],
    stats: [
      { value: "₹12L", label: "Yearly Waste Saved" },
      { value: "1", label: "Operational ERP Layer" },
      { value: "25+", label: "Core Users Supported" },
    ],
    locationSlugs: ["pune", "chicago", "munich"],
    serviceSlugs: ["erp-development", "logistics-supply-chain"],
  },
];

export const getCaseStudyBySlug = (slug: string) =>
  caseStudies.find((caseStudy) => caseStudy.slug === slug);
